import React, { Component } from 'react';
import AIChatbotSetting from '../company/component/AIChatbotSetting';
import AILogsHistory from '../company/component/AILogsHistory';
import AIDocumentsManager from '../company/component/AIDocumentsManager';
// NEW: Nexa AI Sales Agent components
import GeneralInfoTab from './components/GeneralInfoTab';
import SalesStagesTab from './components/SalesStagesTab';
import GuardrailsTab from './components/GuardrailsTab';
import FAQPriorityTab from './components/FAQPriorityTab';
import VersionHistoryTab from './components/VersionHistoryTab';
import ProductKnowledgeTab from './components/ProductKnowledgeTab';
import AIArchitectTab from './components/AIArchitectTab';
import AgentBlueprintView from './components/AgentBlueprintView';
import ConversationAgentBuilderTab from './components/conversation-agent-builder/ConversationAgentBuilderTab';
import { aiConfigApi } from '../../../lib/aiConfigApi';
import { 
  ThunderboltOutlined, 
  ArrowLeftOutlined, 
  HistoryOutlined, 
  CheckCircleOutlined,
  SettingOutlined,
  RobotOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { 
  Tabs, Card, Spin, message, Button, Tag, Space, Switch, Radio, Layout, Row, Col, Modal
} from 'antd';

const { Header, Content } = Layout;

const { TabPane } = Tabs;

const isMissingAgentConfig = ({ response, error }) => {
  if (response?.data) return false;

  const statusValues = [
    response?.status,
    response?.statusCode,
    response?.status_code,
    response?.code,
    error?.status,
    error?.statusCode,
    error?.status_code,
    error?.code,
    error?.err?.status,
    error?.err?.statusCode,
    error?.err?.status_code,
    error?.err?.code,
  ];
  const errorMessages = [
    response?.message,
    error?.message,
    error?.err?.message,
  ].filter(Boolean);

  return response?.code === 200
    || statusValues.some((status) => Number(status) === 404)
    || errorMessages.some((text) => /not found|không tìm thấy/i.test(String(text)));
};

export default class AIView extends Component {
  constructor(props) {
    super(props);
    this.agentCreationSuggestionShownForCompany = null;
    this.state = {
      activeTab: 'strategy',
      activeStrategyTab: 'persona',
      configMode: 'manual', // 'manual' or 'architect'
      loading: true,
      saving: false,
      // Main config object
      config: {
        id: 0,
        isEnabled: false,
        visionEnabled: false,
        agentName: '',
        agentRole: '',
        agentPersonality: '',
        systemPrompt: '',
        greetingMessage: '',
        avatarUrl: '',
        language: 'vi',
        order_tools_enabled: false,
        order_tool_allowlist: [],
        tools: {
          product_catalog: {
            enabled: true,
            max_items: 5,
            match_mode: 'keyword',
          },
        },
        style: {
          emoji_level: 50,
          formality_level: 50,
          response_length: 50,
        },
        strategy: {
          name: 'consultative',
          tone: '',
          objection_handling_guide: '',
          closing_tactics: [],
          key_phrases: [],
          stage_prompts: {},
        },
        guardrails: {
          forbidden_topics: [],
          forbidden_competitors: [],
          max_discount_percent: 15,
          pii_filter_enabled: true,
          max_response_length: 500,
          fallback_message: '',
        },
        automationRules: [],
        sales_policies: [],
      },
      // Separate data
      stages: [],
      faqs: [],
      versions: [],
      currentVersion: null,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.companySelected !== this.props.companySelected) {
      this.loadData();
    }
  }

  loadData = async () => {
    const companyId = this.props.companySelected?.company_id;
    if (!companyId) return;

    this.setState({ loading: true });

    try {
      const [configResult, stagesRes, faqsRes, versionsRes] = await Promise.all([
        aiConfigApi.getConfig(companyId)
          .then((response) => ({ response, error: null }))
          .catch((error) => ({ response: null, error })),
        aiConfigApi.getStages(companyId).catch(() => ({ data: [] })),
        aiConfigApi.getFAQs(companyId).catch(() => ({ data: [] })),
        aiConfigApi.getVersions(companyId).catch(() => ({ data: [] })),
      ]);
      const configRes = configResult.response || {};
      const agentConfigMissing = isMissingAgentConfig(configResult);

      // Get current version from config
      const currentVersion = configRes.data?.currentVersion || configRes.data?.current_version || 1;

      this.setState({
        config: configRes.data || this.state.config,
        stages: stagesRes.data || [],
        faqs: faqsRes.data || [],
        versions: versionsRes.data || [],
        currentVersion,
      }, () => {
        if (agentConfigMissing) {
          this.showAgentCreationSuggestion(companyId);
        } else if (configRes.data) {
          this.agentCreationSuggestionShownForCompany = null;
        }
      });
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Không thể tải dữ liệu');
    } finally {
      this.setState({ loading: false });
    }
  };

  showAgentCreationSuggestion = (companyId) => {
    if (this.agentCreationSuggestionShownForCompany === companyId) return;
    this.agentCreationSuggestionShownForCompany = companyId;

    Modal.confirm({
      title: 'Bạn chưa có Agent AI',
      content: 'Bạn có thể tạo Agent bằng cách mô tả mong muốn cho AI Architect. Bạn có muốn bắt đầu ngay không?',
      okText: 'Tạo Agent bằng AI',
      cancelText: 'Để sau',
      onOk: () => this.setState({ configMode: 'architect' }),
    });
  };

  // Handle config field changes
  handleConfigChange = (field, value) => {
    this.setState((prev) => ({
      config: {
        ...prev.config,
        [field]: value,
      },
    }));
  };

  // Handle nested config changes
  handleNestedChange = (parent, field, value) => {
    this.setState((prev) => ({
      config: {
        ...prev.config,
        [parent]: {
          ...prev.config[parent],
          [field]: value,
        },
      },
    }));
  };

  handleProductCatalogToolChange = (changes) => {
    this.setState((prev) => ({
      config: {
        ...prev.config,
        tools: {
          ...(prev.config.tools || {}),
          product_catalog: {
            enabled: true,
            max_items: 5,
            match_mode: 'keyword',
            ...((prev.config.tools || {}).product_catalog || {}),
            ...changes,
          },
        },
      },
    }));
  };

  // Handle quick toggle enabled
  handleToggleEnabled = async (checked) => {
    const { config } = this.state;
    const companyId = this.props.companySelected?.company_id;
    if (!companyId) return;

    this.setState(prev => ({
      config: { ...prev.config, isEnabled: checked }
    }));

    try {
      await aiConfigApi.updateConfig({
        companyId,
        isEnabled: checked
      });
      message.success(checked ? 'Đã bật Agent AI' : 'Đã tạm ngưng Agent AI');
    } catch (error) {
      console.error('Error toggling status:', error);
      message.error('Không thể thay đổi trạng thái');
      // Rollback on error
      this.setState(prev => ({
        config: { ...prev.config, isEnabled: !checked }
      }));
    }
  };

  // Save config
  handleSave = async () => {
    const { config } = this.state;
    const companyId = this.props.companySelected?.company_id;
    if (!companyId) return;

    this.setState({ saving: true });

    try {
      await aiConfigApi.updateConfig({
        companyId,
        ...config,
      });
      message.success('Đã lưu cấu hình thành công');
      this.loadData(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving config:', error);
      message.error('Không thể lưu cấu hình');
    } finally {
      this.setState({ saving: false });
    }
  };

  // Handle stages change
  handleStagesChange = (stages) => {
    this.setState({ stages });
  };

  // Save stages
  handleSaveStages = async () => {
    const { stages } = this.state;
    const companyId = this.props.companySelected?.company_id;
    if (!companyId) return;

    try {
      // Update each stage
      for (const stage of stages) {
        if (stage.id && !String(stage.id).startsWith('stage_')) {
          // Existing stage - update
          await aiConfigApi.updateStage(stage.id, stage);
        } else {
          // New stage - create
          await aiConfigApi.createStage({
            companyId,
            ...stage,
            name: stage.name || stage.display_name?.toUpperCase().replace(/\s+/g, '_'),
          });
        }
      }
      message.success('Đã lưu stages thành công');
      this.loadData();
    } catch (error) {
      console.error('Error saving stages:', error);
      message.error('Không thể lưu stages');
    }
  };

  // Handle FAQs change
  handleFAQsChange = (faqs) => {
    this.setState({ faqs });
  };

  // Save FAQs
  handleSaveFAQs = async () => {
    const { faqs } = this.state;
    const companyId = this.props.companySelected?.company_id;
    if (!companyId) return;

    this.setState({ saving: true });

    try {
      console.log(`[AIView] Saving ${faqs.length} FAQs...`);
      const savePromises = faqs.map(faq => {
        if (faq.id && !String(faq.id).startsWith('faq_')) {
          // Cập nhật FAQ đã có
          return aiConfigApi.updateFAQ(faq.id, faq);
        } else {
          // Tạo FAQ mới
          return aiConfigApi.createFAQ({
            companyId,
            question: faq.question,
            answer: faq.answer,
            priority: faq.priority || 0,
            tags: faq.tags || [],
            isActive: faq.isActive !== false,
          });
        }
      });

      await Promise.all(savePromises);
      message.success('Đã lưu tất cả câu hỏi FAQ thành công');
      await this.loadData(); // Load lại để cập nhật ID thật từ DB
    } catch (error) {
      console.error('Error saving FAQs:', error);
      message.error('Không thể lưu một số câu hỏi FAQ. Vui lòng thử lại.');
    } finally {
      this.setState({ saving: false });
    }
  };

  // Publish new version
  handlePublishVersion = async (notes) => {
    const companyId = this.props.companySelected?.company_id;
    if (!companyId) return;

    try {
      await aiConfigApi.publishVersion({
        companyId,
        notes,
        createdBy: this.props.user?.name || 'Admin',
      });
      message.success('Đã publish version mới thành công');
      this.loadData();
    } catch (error) {
      console.error('Error publishing version:', error);
      message.error('Không thể publish version');
    }
  };

  // Rollback to version
  handleRollbackVersion = async (versionId) => {
    const companyId = this.props.companySelected?.company_id;
    if (!companyId) return;

    try {
      await aiConfigApi.rollbackVersion(versionId, {
        companyId,
        createdBy: this.props.user?.name || 'Admin',
      });
      message.success('Đã rollback thành công');
      this.loadData();
    } catch (error) {
      console.error('Error rolling back:', error);
      message.error('Không thể rollback');
    }
  };

  render() {
    if (!this.props.companySelected) return null;

    const { 
      activeTab, loading, saving, config, stages, 
      faqs, versions, currentVersion, configMode 
    } = this.state;

    if (loading) {
      return (
        <div className="list-connect content-m" style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Đang tải dữ liệu...</div>
        </div>
      );
    }

    const manualContent = (
      <Tabs
        activeKey={activeTab}
        onChange={(key) => this.setState({ activeTab: key })}
        type="card"
        size="large"
      >
        <TabPane tab="Chiến lược" key="strategy">
          <Tabs
            activeKey={this.state.activeStrategyTab}
            onChange={(key) => this.setState({ activeStrategyTab: key })}
            size="middle"
            style={{ marginBottom: 16 }}
          >
            <TabPane tab="1. Thông tin chung" key="persona">
              <GeneralInfoTab
                config={config}
                onChange={this.handleConfigChange}
                onSave={this.handleSave}
                loading={saving}
              />
            </TabPane>
            <TabPane tab="3. Quy trình" key="stages">
              <SalesStagesTab
                stages={stages}
                onChange={this.handleStagesChange}
                onSave={this.handleSaveStages}
                loading={saving}
              />
            </TabPane>
            <TabPane tab="4. Rào chắn" key="guardrails">
              <GuardrailsTab
                guardrails={config.guardrails}
                onChange={this.handleNestedChange}
                onSave={this.handleSave}
                loading={saving}
              />
            </TabPane>
            <TabPane tab="5. Câu hỏi thường gặp" key="faq">
              <FAQPriorityTab
                faqs={faqs}
                onChange={this.handleFAQsChange}
                onSave={this.handleSaveFAQs}
                loading={saving}
              />
            </TabPane>
          </Tabs>
        </TabPane>

        <TabPane tab="Kiến thức chung" key="documents">
          <AIDocumentsManager {...this.props} />
        </TabPane>

        <TabPane tab="Kiến thức sản phẩm" key="product-knowledge">
          <ProductKnowledgeTab
            companyId={this.props.companySelected?.company_id}
            config={config}
            onConfigChange={this.handleConfigChange}
            onToolChange={this.handleProductCatalogToolChange}
            onSave={this.handleSave}
            saving={saving}
          />
        </TabPane>

{/* <TabPane tab="Cấu hình" key="config">
  <AIChatbotSetting {...this.props} />
</TabPane> */}

        <TabPane tab="Version" key="versions">
          <VersionHistoryTab
            versions={versions}
            currentVersion={currentVersion}
            onPublish={this.handlePublishVersion}
            onRollback={this.handleRollbackVersion}
            loading={saving}
          />
        </TabPane>

        {/* <TabPane tab="Lịch sử log" key="logs">
          <AILogsHistory {...this.props} />
        </TabPane> */}
      </Tabs>
    );


    return (
      <div className="list-connect content-m" style={{ 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%', 
        backgroundColor: '#f5f7f9', 
        height: 'calc(100vh - 100px)', // Precise height to fit in admin layout
        padding: '16px',
        overflow: 'hidden'
      }}>
        {/* NEW HEADER - Image 2 style */}
        <div style={{ 
          backgroundColor: '#fff', 
          padding: '10px 20px', 
          borderRadius: '8px', 
          marginBottom: 16, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          border: '1px solid #f0f0f0',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>
                  {config.agentName || 'Chưa đặt tên'}
                </span>
                <Tag style={{ borderRadius: '8px', padding: '0 8px', backgroundColor: '#f5f5f5', border: '1px solid #e8e8e8', color: '#8c8c8c', fontSize: '11px' }}>
                  <HistoryOutlined style={{ marginRight: 2 }} /> v{currentVersion || 1}
                </Tag>
              </div>
              <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>
                ID: {config.id || 1} • Nexa AI Agent Architect
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: '#f0f2f5', 
            padding: '4px', 
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 2,
            maxWidth: '100%',
            border: '1px solid #e0e0e0'
          }}>
            <Button 
              type="text"
              aria-pressed={configMode === 'manual'}
              onClick={() => this.setState({ configMode: 'manual' })}
              style={{ 
                borderRadius: '6px', 
                fontWeight: 600,
                width: 'auto',
                padding: '0 10px',
                fontSize: '13px',
                height: '32px',
                backgroundColor: configMode === 'manual' ? '#fff' : 'transparent',
                color: configMode === 'manual' ? '#262626' : '#595959',
                boxShadow: configMode === 'manual' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              Cấu hình thủ công
            </Button>
            <Button
              type="text"
              aria-pressed={configMode === 'architect'}
              onClick={() => this.setState({ configMode: 'architect' })}
              style={{
                borderRadius: '6px',
                fontWeight: 600,
                width: 'auto',
                padding: '0 10px',
                fontSize: '13px',
                height: '32px',
                backgroundColor: configMode === 'architect' ? '#fff' : 'transparent',
                color: configMode === 'architect' ? '#1677ff' : '#595959',
                boxShadow: configMode === 'architect' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              icon={<ThunderboltOutlined style={{ color: configMode === 'architect' ? '#1677ff' : '#595959' }} />}
            >
              AI Architect
            </Button>
            <Button
              type="text"
              aria-pressed={configMode === 'conversation'}
              onClick={() => this.setState({ configMode: 'conversation' })}
              style={{
                borderRadius: '6px',
                fontWeight: 600,
                width: 'auto',
                padding: '0 10px',
                fontSize: '13px',
                height: '32px',
                backgroundColor: configMode === 'conversation' ? '#fff' : 'transparent',
                color: configMode === 'conversation' ? '#2563eb' : '#595959',
                boxShadow: configMode === 'conversation' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              Tạo Agent từ Hội thoại
            </Button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Tag 
              style={{ 
                borderRadius: '8px', 
                padding: '0 8px', 
                border: 'none', 
                fontWeight: 600,
                fontSize: '11px',
                backgroundColor: config.isEnabled ? '#f6ffed' : '#fff1f0',
                color: config.isEnabled ? '#52c41a' : '#f5222d',
                margin: 0
              }}
            >
              {config.isEnabled ? <CheckCircleOutlined style={{ marginRight: 4 }} /> : null}
              {config.isEnabled ? "Đang chạy" : "Tạm ngưng"}
            </Tag>
            <Switch 
              size="small" 
              checked={config.isEnabled} 
              onChange={this.handleToggleEnabled}
            />
          </div>
        </div>

        {/* CONTENT AREA - FLEX: 1 ENABLES INTERNAL SCROLLING */}
        <div style={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
          {configMode === 'manual' ? (
            <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
              <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                {manualContent}
              </div>
            </div>
          ) : configMode === 'conversation' ? (
            <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
              <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <ConversationAgentBuilderTab
                  companyId={this.props.companySelected?.company_id}
                  userName={this.props.user?.name || 'Admin'}
                />
              </div>
            </div>
          ) : (
            <Row gutter={16} style={{ height: '100%', margin: 0 }}>
              {/* LEFT: AI ARCHITECT CHAT - INDEPENDENT SCROLL */}
              <Col span={12} style={{ height: '100%', padding: '0 8px 0 0' }}>
                <div style={{
                  height: '100%',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                  border: '1px solid #f0f0f0',
                  overflow: 'hidden'
                }}>
                  <AIArchitectTab
                    {...this.props}
                    config={config}
                    onRefresh={this.loadData}
                  />
                </div>
              </Col>

              {/* RIGHT: CURRENT CONFIG VIEW - INDEPENDENT SCROLL */}
              <Col span={12} style={{ height: '100%', padding: '0 0 0 8px' }}>
                <div style={{
                  height: '100%',
                  overflowY: 'auto',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                  border: '1px solid #f0f0f0'
                }}>
                  <AgentBlueprintView
                    config={config}
                    stages={stages}
                    faqs={faqs}
                  />
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    );
  }
}
